/* eslint-disable no-restricted-globals */

import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button, Col, FormFeedback, FormGroup, Input, Label, Row } from "reactstrap";
import Switch from "react-switch";

/**
 * @typedef {Object} AdditionalFieldSelectConfigurationOption
 * @property {string} id
 * @property {string} label
 * @property {string} value
 */

/**
 * @typedef {Object} AdditionalFieldSelectConfiguration
 * @property {AdditionalFieldSelectConfigurationOption[]} options
 * @property {boolean} isMulti
 */

/**
 * @typedef {Object} AdditionalFieldSelectConfigurationProps
 * @property {AdditionalFieldSelectConfiguration} configuration
 * @property {function(AdditionalFieldSelectConfiguration): void} onChange
 * @property {Record<string, string>} errors
 */

/**
 * @param {AdditionalFieldSelectConfigurationProps} props
 * @returns {JSX.Element}
 */
export function AdditionalFieldSelectConfiguration({ configuration, onChange, errors }) {
  const { t } = useTranslation()

  const handleChange = useCallback((newOption) => {
    const options = configuration.options.map((option) => {
      if (option.id === newOption.id) return newOption;
      return option;
    });

    onChange({ ...configuration, options });
  }, [configuration, onChange])

  const handleRemove = useCallback((option) => {
    const options = configuration.options.filter(({ id }) => id !== option.id);
    onChange({ ...configuration, options });
  }, [configuration, onChange])

  const handleAdd = useCallback(() => {
    const options = [
      ...configuration.options,
      { id: self.crypto.randomUUID(), label: "Nova Opção", value: "Nova Opção" }
    ];
    onChange({ ...configuration, options });
  }, [configuration, onChange])

  const handleChangeIsMulti = useCallback((isMulti) => {
    onChange({ ...configuration, isMulti });
  }, [configuration, onChange])

  return (
    <div>
      <Row>
        <Col>
          <Label>
            {t("Options") + " *"}
          </Label>
        </Col>
        <Col sm='auto'>
          <FormGroup className="d-flex justify-content-between align-items-center">
            <Label className="mb-0 mr-2">
              {t("Is Multi")}
            </Label>
            <Switch
              className="d-flex align-self-center"
              onColor="#34c38f"
              offColor="#bfc8e2"
              onHandleColor="#222736"
              offHandleColor="#32394E"
              checked={configuration.isMulti}
              onChange={handleChangeIsMulti}
              handleDiameter={10}
              width={28}
              height={16}
              checkedIcon={false}
              uncheckedIcon={false}
              disabled
            />
          </FormGroup>
        </Col>
      </Row>
      {configuration.options.map((option, index) => (
        <AdditionalFieldSelectSettingsOption
          key={`additional-field:select:settings:options:${option.id}`}
          index={index}
          option={option}
          onChange={handleChange}
          onRemove={handleRemove}
          errors={errors}
        />
      ))}
      <div className="mb-3">
        <Button size='sm' type="button" color="primary" onClick={handleAdd}>
          <i className="mdi mdi-plus mr-1" />
          {t('Add Select Option')}
        </Button>
      </div>
    </div>
  );
}

/**
 * @typedef {Object} AdditionalFieldSelectConfigurationOptionProps
 * @property {number} index
 * @property {AdditionalFieldSelectConfigurationOption} option
 * @property {Record<string, string>} errors
 * @property {function(AdditionalFieldSelectConfigurationOption): void} onChange
 * @property {function(AdditionalFieldSelectConfigurationOption): void} onRemove
 */

/**
 * @param {AdditionalFieldSelectConfigurationOptionProps} props
 * @returns {JSX.Element}
 */
export function AdditionalFieldSelectSettingsOption({ index, option, errors, onChange, onRemove }) {
  const { t } = useTranslation()

  const handleClickRemove = useCallback(() => onRemove(option), [onRemove, option]);

  const handleChangeLabel = useCallback((event) => {
    const value = event.target.value
    if (option.label === option.value) onChange({ ...option, label: value, value });
    else onChange({ ...option, label: event.target.value })
  }, [onChange, option]);

  const handleChangeValue = useCallback((event) => {
    const value = event.target.value
    onChange({ ...option, value })
  }, [onChange, option])

  const labelError = errors?.[`configuration.options[${index}].label`];
  const valueError = errors?.[`configuration.options[${index}].value`];

  return (
    <Row>
      <Col>
        <FormGroup>
          <Label>{t('Label')}:</Label>
          <Input invalid={!!labelError} placeholder={t('Label')} onChange={handleChangeLabel} value={option.label} />
          {labelError && <FormFeedback>{labelError}</FormFeedback>}
        </FormGroup>
      </Col>
      <Col>
        <FormGroup>
          <Label>{t('Value')}:</Label>
          <Input invalid={!!valueError} placeholder={t('Value')} onChange={handleChangeValue} value={option.value} />
          {valueError && <FormFeedback>{valueError}</FormFeedback>}
        </FormGroup>
      </Col>
      <Col xs='auto' className='d-flex justify-content-center align-items-center'>
        <i onClick={handleClickRemove} className={`bx bxs-trash icon-trash-tab`} />
      </Col>
    </Row>
  );
}
